import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Search,
  Edit,
  Trash2,
  Eye,
  Filter,
  Package,
  Leaf,
  Zap,
  Recycle,
  AlertTriangle,
  Star,
  RefreshCw,
  Download,
  FileText,
  Library,
  Upload
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import { unifiedLibraryService } from '@/services/unifiedLibraryService';
import { dataSyncEvents, useDataSync } from '@/utils/dataSyncEvents';
import { UserMaterialData } from '@/types/unifiedLibrary';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { supabase } from '@/integrations/supabase/client';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface OrganizationMaterialsManagerProps {
  onTabChange?: (tab: string) => void;
}

export const OrganizationMaterialsManager = ({ onTabChange }: OrganizationMaterialsManagerProps) => {
  const [userMaterials, setUserMaterials] = useState<UserMaterialData[]>([]);
  const [filteredMaterials, setFilteredMaterials] = useState<UserMaterialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterScope, setFilterScope] = useState('all');
  const [selectedMaterial, setSelectedMaterial] = useState<UserMaterialData | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<UserMaterialData | null>(null);
  const [editFormData, setEditFormData] = useState({
    custom_density: '',
    custom_carbon_footprint: '',
    custom_cost_per_unit: '',
    unit: '',
    notes: ''
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const { user } = useAuth();

  // ── Use organizationId everywhere instead of user.id ───────────────────────
  // Admin:       organizationId === user.id
  // Team member: organizationId === admin's user.id (their org's identity)
  const { organizationId, loading: orgLoading } = useOrganization();

  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();

  useEffect(() => {
    if (user && organizationId && !orgLoading && !dataLoaded) {
      loadData();
    }
  }, [user, organizationId, orgLoading, dataLoaded]);

  // Subscribe to data changes using organizationId so all org members stay in sync
  useDataSync(organizationId || '', () => {
    if (user && organizationId) {
      loadData();
    }
  });

  useEffect(() => {
    applyFiltersAndSearch();
  }, [userMaterials, searchTerm, filterCategory, filterScope]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (!user || !organizationId) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to access your materials data',
          variant: 'destructive'
        });
        setUserMaterials([]);
        setCategories([]);
        setDataLoaded(false);
        return;
      }

      // ── organizationId: team members see the same data as their admin ────────
      const { data: materials, error: materialsError } = await supabase
        .from('user_materials_data')
        .select('*')
        .eq('organization_id', organizationId)   // ← was user.id
        .order('created_at', { ascending: false });

      if (materialsError) throw materialsError;

      const transformedMaterials: UserMaterialData[] = (materials as any || []).map(material => ({
        id: material.id,
        organization_id: material.organization_id,
        material_id: material.material_id,
        name: material.name,
        category: material.category,
        subcategory: material.subcategory,
        description: material.description,
        unit: material.unit,
        density: material.density,
        carbon_footprint: material.carbon_footprint,
        cost_per_unit: material.cost_per_unit,
        currency: material.currency,
        custom_density: material.custom_density,
        custom_carbon_footprint: material.custom_carbon_footprint,
        custom_cost_per_unit: material.custom_cost_per_unit,
        custom_metadata: material.custom_metadata,
        is_custom: material.is_custom,
        created_at: material.created_at,
        updated_at: material.updated_at
      }));

      const categoriesData = await unifiedLibraryService.getMaterialCategories();

      setUserMaterials(transformedMaterials);
      setCategories(categoriesData);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading organization materials:', error);
      toast({
        title: 'Error',
        description: 'Failed to load organization materials',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...userMaterials];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const searchableFields = [
          item.name,
          item.category,
          item.subcategory,
          item.description,
          item.unit,
          item.currency,
          item.custom_density?.toString(),
          item.custom_carbon_footprint?.toString(),
          item.custom_cost_per_unit?.toString(),
          item.custom_metadata?.data_source,
          item.custom_metadata?.notes
        ].filter(Boolean);
        return searchableFields.some(field => field && field.toString().toLowerCase().includes(term));
      });
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => item.category === filterCategory);
    }

    if (filterScope !== 'all') {
      filtered = filtered.filter(item => item.custom_metadata?.scope === filterScope);
    }

    setFilteredMaterials(filtered);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);
  const clearFilters = () => { setSearchTerm(''); setFilterCategory('all'); setFilterScope('all'); };

  // ─── EDIT ────────────────────────────────────────────────────────────────────
  const handleEdit = (material: UserMaterialData) => {
    setSelectedMaterial(material);
    setEditFormData({
      custom_density: material.custom_density?.toString() || material.density?.toString() || '',
      custom_carbon_footprint: material.custom_carbon_footprint?.toString() || material.carbon_footprint?.toString() || '',
      custom_cost_per_unit: material.custom_cost_per_unit?.toString() || material.cost_per_unit?.toString() || '',
      unit: material.unit || '',
      notes: material.custom_metadata?.notes || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedMaterial || !user || !organizationId) return;

    try {
      // ── organizationId for the update filter ──────────────────────────────
      const { error } = await supabase
        .from('user_materials_data')
        .update({
          custom_density: parseFloat(editFormData.custom_density) || null,
          custom_carbon_footprint: parseFloat(editFormData.custom_carbon_footprint) || null,
          custom_cost_per_unit: parseFloat(editFormData.custom_cost_per_unit) || null,
          unit: editFormData.unit || selectedMaterial.unit,
          custom_metadata: {
            ...selectedMaterial.custom_metadata,
            notes: editFormData.notes || null
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedMaterial.id)
        .eq('organization_id', organizationId);   // ← was user.id

      if (error) throw error;

      // ── Emit with organizationId so all org members get the sync event ────
      dataSyncEvents.emitMaterialChange('updated', selectedMaterial.id, organizationId);

      const updatedMaterials = userMaterials.map(item =>
        item.id === selectedMaterial.id
          ? {
              ...item,
              custom_density: parseFloat(editFormData.custom_density) || item.custom_density,
              custom_carbon_footprint: parseFloat(editFormData.custom_carbon_footprint) || item.custom_carbon_footprint,
              custom_cost_per_unit: parseFloat(editFormData.custom_cost_per_unit) || item.custom_cost_per_unit,
              unit: editFormData.unit || item.unit,
              custom_metadata: { ...item.custom_metadata, notes: editFormData.notes || null },
              updated_at: new Date().toISOString()
            }
          : item
      );
      setUserMaterials(updatedMaterials);

      toast({ title: 'Success', description: 'Material updated successfully' });
      setIsEditModalOpen(false);
      setSelectedMaterial(null);
    } catch (error) {
      console.error('Error updating material:', error);
      toast({ title: 'Error', description: 'Failed to update material', variant: 'destructive' });
    }
  };

  // ─── DELETE ──────────────────────────────────────────────────────────────────
  const handleDelete = (material: UserMaterialData) => {
    setMaterialToDelete(material);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!materialToDelete || !user || !organizationId) return;

    try {
      // ── organizationId for the delete filter ──────────────────────────────
      const { error } = await supabase
        .from('user_materials_data')
        .delete()
        .eq('id', materialToDelete.id)
        .eq('organization_id', organizationId);   // ← was user.id

      if (error) throw error;

      // ── Emit with organizationId ───────────────────────────────────────────
      dataSyncEvents.emitMaterialChange('deleted', materialToDelete.id, organizationId);

      setUserMaterials(userMaterials.filter(item => item.id !== materialToDelete.id));
      toast({ title: 'Success', description: 'Material deleted successfully' });
      setIsDeleteDialogOpen(false);
      setMaterialToDelete(null);
    } catch (error) {
      console.error('Error deleting material:', error);
      toast({ title: 'Error', description: 'Failed to delete material', variant: 'destructive' });
      setIsDeleteDialogOpen(false);
      setMaterialToDelete(null);
    }
  };

  // ─── VIEW ────────────────────────────────────────────────────────────────────
  const handleView = (material: UserMaterialData) => {
    setSelectedMaterial(material);
    setIsViewModalOpen(true);
  };

  // ─── REFRESH ─────────────────────────────────────────────────────────────────
  const handleManualRefresh = async () => {
    if (user && organizationId) {
      setDataLoaded(false);
      await loadData();
      toast({ title: 'Success', description: 'Materials data refreshed successfully' });
    }
  };

  // ─── HELPERS ─────────────────────────────────────────────────────────────────
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Fossil Fuels': return <Package className="h-4 w-4" />;
      case 'Biomass': return <Leaf className="h-4 w-4" />;
      case 'Metals': return <Zap className="h-4 w-4" />;
      case 'Waste': return <Recycle className="h-4 w-4" />;
      case 'Plastics': return <Package className="h-4 w-4" />;
      case 'Wood & Paper': return <Leaf className="h-4 w-4" />;
      case 'Textiles': return <Package className="h-4 w-4" />;
      case 'Electronics': return <Zap className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'Scope 1': return 'bg-red-100 text-red-800';
      case 'Scope 2': return 'bg-orange-100 text-orange-800';
      case 'Scope 3': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSustainabilityColor = (rating: number) => {
    if (rating >= 4) return 'bg-green-100 text-green-800';
    if (rating >= 3) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`h-3 w-3 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  // ─── EXPORTS ─────────────────────────────────────────────────────────────────
  const handleExportCSV = async () => {
    if (!(await guardDownload())) return;

    try {
      const headers = ['Name','Category','Subcategory','Density','Unit','Carbon Footprint','Cost per Unit','Currency','Scope','Sustainability Rating','Description','Notes','Created At','Updated At'];
      const csvContent = [
        headers.join(','),
        ...filteredMaterials.map(m => [
          `"${m.name||''}"`,`"${m.category||''}"`,`"${m.subcategory||''}"`,
          `"${m.custom_density||''}"`,`"${m.unit||''}"`,`"${m.custom_carbon_footprint||''}"`,
          `"${m.custom_cost_per_unit||''}"`,`"${m.currency||''}"`,`"${m.custom_metadata?.scope||''}"`,
          `"${m.custom_metadata?.sustainability_rating||''}"`,`"${m.description||''}"`,
          `"${m.custom_metadata?.notes||''}"`,`"${m.created_at||''}"`,`"${m.updated_at||''}"`
        ].join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `organization_materials_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: 'Export Successful', description: 'Materials exported to CSV.' });
    } catch (error) {
      toast({ title: 'Export Error', description: 'Failed to export CSV.', variant: 'destructive' });
    }
  };

  const generateAIInsights = () => {
    if (filteredMaterials.length === 0) return "No materials data available for analysis.";
    const insights = [];
    const total = filteredMaterials.length;
    const cats = [...new Set(filteredMaterials.map(m => m.category).filter(Boolean))];
    const scopes = [...new Set(filteredMaterials.map(m => m.custom_metadata?.scope).filter(Boolean))];
    const ratings = filteredMaterials.map(m => m.custom_metadata?.sustainability_rating).filter(Boolean);

    insights.push(`<h3>Dataset Overview</h3><p>This report contains <strong>${total}</strong> materials across <strong>${cats.length}</strong> categories spanning <strong>${scopes.length}</strong> emission scopes with avg sustainability rating of <strong>${ratings.length > 0 ? (ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length).toFixed(1) : 'N/A'}/5</strong>.</p>`);

    if (cats.length > 0) {
      const cc = cats.map(c => ({ category: c, count: filteredMaterials.filter(m => m.category === c).length })).sort((a,b)=>b.count-a.count);
      insights.push(`<h3>Category Distribution</h3><p>Most represented: <strong>${cc[0].category}</strong> (${cc[0].count} materials, ${Math.round(cc[0].count/total*100)}%).</p>`);
    }

    let avgRating = 0;
    if (ratings.length > 0) {
      avgRating = ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length;
      const highPct = Math.round(ratings.filter((r: number) => r >= 4).length / ratings.length * 100);
      insights.push(`<h3>Sustainability Performance</h3><p>Average rating <strong>${avgRating.toFixed(1)}/5</strong>. ${highPct}% of materials rated 4+ stars — ${highPct>=60?'strong':highPct>=40?'moderate':'needs improvement'}.</p>`);
    }

    const withCost = filteredMaterials.filter(m => m.custom_cost_per_unit);
    let costRange = { min: 0, max: 0 };
    if (withCost.length > 0) {
      const avg = withCost.reduce((s,m) => s + (m.custom_cost_per_unit||0), 0) / withCost.length;
      costRange = { min: Math.min(...withCost.map(m=>m.custom_cost_per_unit||0)), max: Math.max(...withCost.map(m=>m.custom_cost_per_unit||0)) };
      insights.push(`<h3>Cost Analysis</h3><p>Avg cost/unit: <strong>${avg.toFixed(2)}</strong> · Range: ${costRange.min.toFixed(2)}–${costRange.max.toFixed(2)} (${costRange.max/costRange.min>10?'significant':'moderate'} variability).</p>`);
    }

    insights.push(`<h3>Recommendations</h3><ul>${scopes.length<3?'<li>Expand scope coverage for comprehensive reporting.</li>':''}${avgRating>0&&avgRating<3.5?'<li>Source materials with higher sustainability ratings.</li>':''}${withCost.length>0&&costRange.max/costRange.min>10?'<li>Cost optimization opportunities via strategic sourcing.</li>':''}<li>Establish periodic reviews aligned to organizational goals.</li></ul>`);
    return insights.join('');
  };

  const buildReportHTML = (insights: string) => `
    <!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Materials Report</title>
    <style>
      body{font-family:'Segoe UI',sans-serif;margin:20px;line-height:1.6;color:#333}
      .header{text-align:center;padding:20px;background:linear-gradient(135deg,#16a34a,#15803d);color:white;border-radius:10px;margin-bottom:30px}
      table{width:100%;border-collapse:collapse;margin:20px 0;font-size:12px}
      th,td{border:1px solid #e0e0e0;padding:10px 8px;text-align:left;vertical-align:top}
      th{background:linear-gradient(135deg,#16a34a,#15803d);color:white;font-size:11px;text-transform:uppercase}
      tr:nth-child(even){background:#f8f9fa}
      .insights{padding:20px;border:1px solid #e0e0e0;border-radius:8px;margin-top:30px}
      .insights h3{color:#16a34a;border-bottom:2px solid #16a34a;padding-bottom:5px}
      .footer{text-align:center;padding:20px;background:#f8f9fa;border-radius:8px;margin-top:30px;font-size:12px;color:#666}
    </style></head><body>
    <div class="header">
      <h1>Organization Materials Report</h1>
      <p>Generated: ${new Date().toLocaleString()} · Total: ${filteredMaterials.length} materials</p>
      <p>Generated by: ${user?.user_metadata?.full_name || user?.email || 'N/A'}</p>
    </div>
    <h2 style="color:#16a34a">Materials</h2>
    <table><thead><tr><th>Name</th><th>Category</th><th>Subcategory</th><th>Density</th><th>Unit</th><th>Carbon Footprint</th><th>Cost/Unit</th><th>Scope</th><th>Sustainability</th><th>Notes</th></tr></thead>
    <tbody>${filteredMaterials.map(m=>`<tr>
      <td><strong>${m.name||'N/A'}</strong></td><td>${m.category||'N/A'}</td><td>${m.subcategory||'N/A'}</td>
      <td>${m.custom_density||'N/A'}</td><td>${m.unit||'N/A'}</td>
      <td>${m.custom_carbon_footprint||'N/A'}</td><td>${m.custom_cost_per_unit||'N/A'}</td>
      <td>${m.custom_metadata?.scope||'N/A'}</td>
      <td>${m.custom_metadata?.sustainability_rating?`${m.custom_metadata.sustainability_rating}/5`:'N/A'}</td>
      <td>${m.custom_metadata?.notes||'N/A'}</td>
    </tr>`).join('')}</tbody></table>
    <div class="insights"><h2 style="color:#16a34a">AI-Generated Insights</h2>${insights}</div>
    <div class="footer"><p>GreenCard Sustainability Manager</p></div>
    </body></html>`;

  const handleExportHTML = async () => {
    if (!(await guardDownload())) return;

    try {
      const blob = new Blob([buildReportHTML(generateAIInsights())], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `organization_materials_report_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast({ title: 'Export Successful', description: 'HTML report exported.' });
    } catch (error) {
      toast({ title: 'Export Error', description: 'Failed to export HTML.', variant: 'destructive' });
    }
  };

  const handleExportPDF = async () => {
    if (!(await guardDownload())) return;

    try {
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:absolute;left:-9999px;top:0;width:1200px;background:white;padding:20px';
      tempDiv.innerHTML = buildReportHTML(generateAIInsights());
      document.body.appendChild(tempDiv);
      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: 1200, height: tempDiv.scrollHeight, logging: false });
      document.body.removeChild(tempDiv);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`organization_materials_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast({ title: 'Export Successful', description: 'PDF report exported.' });
    } catch (error) {
      toast({ title: 'Export Error', description: 'Failed to export PDF.', variant: 'destructive' });
    }
  };

  // ─── LOADING STATES ──────────────────────────────────────────────────────────
  if (loading || orgLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>Organization Materials</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading organization materials...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (userMaterials.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Organization Materials</CardTitle>
          <div className="text-sm text-gray-600">Materials you've customized for your organization</div>
        </CardHeader>
        <CardContent>
          <Alert>
            <Package className="h-4 w-4" />
            <AlertDescription>No organization materials found. Add materials from the Materials Library to get started.</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // ─── MAIN RENDER ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Organization Materials ({filteredMaterials.length} items)</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" />CSV</Button>
              <Button variant="outline" size="sm" onClick={handleExportHTML}><FileText className="w-4 h-4 mr-2" />HTML</Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF}><FileText className="w-4 h-4 mr-2" />PDF</Button>
            </div>
          </CardTitle>
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-500" />
              <Input placeholder="Search materials..." value={searchTerm} onChange={handleSearchChange} className="w-64" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Filter by category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterScope} onValueChange={setFilterScope}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Filter by scope" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scopes</SelectItem>
                <SelectItem value="Scope 1">Scope 1</SelectItem>
                <SelectItem value="Scope 2">Scope 2</SelectItem>
                <SelectItem value="Scope 3">Scope 3</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Density</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Carbon Footprint</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Sustainability</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMaterials.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell>
                      <div className="font-medium">{material.name || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{material.subcategory || 'N/A'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(material.category)}
                        <Badge variant="outline">{material.category || 'N/A'}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>{material.custom_density || material.density || 'N/A'}</TableCell>
                    <TableCell>{material.unit || 'N/A'}</TableCell>
                    <TableCell>
                      {material.custom_metadata?.scope
                        ? <Badge className={getScopeColor(material.custom_metadata.scope)}>{material.custom_metadata.scope}</Badge>
                        : <span className="text-gray-400 text-sm">N/A</span>}
                    </TableCell>
                    <TableCell>
                      {material.custom_carbon_footprint || material.carbon_footprint
                        ? <span className="text-sm">{material.custom_carbon_footprint || material.carbon_footprint} kg CO₂e</span>
                        : <span className="text-gray-400 text-sm">N/A</span>}
                    </TableCell>
                    <TableCell>
                      {material.custom_cost_per_unit || material.cost_per_unit
                        ? <span className="text-sm">{material.custom_cost_per_unit || material.cost_per_unit} {material.currency || 'USD'}</span>
                        : <span className="text-gray-400 text-sm">N/A</span>}
                    </TableCell>
                    <TableCell>
                      {material.custom_metadata?.sustainability_rating
                        ? <div className="flex items-center gap-2">
                            <div className="flex">{renderStars(material.custom_metadata.sustainability_rating)}</div>
                            <Badge className={getSustainabilityColor(material.custom_metadata.sustainability_rating)}>
                              {material.custom_metadata.sustainability_rating}/5
                            </Badge>
                          </div>
                        : <span className="text-gray-400 text-sm">N/A</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleView(material)}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(material)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(material)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredMaterials.length === 0 && (
              <div className="text-center py-12">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Materials Available</h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm || filterCategory !== 'all' || filterScope !== 'all'
                    ? 'No materials match your current filters.'
                    : "You haven't added any materials yet."}
                </p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => onTabChange?.('materials')}><Library className="h-4 w-4 mr-2" />Browse Materials</Button>
                  <Button variant="outline" onClick={() => onTabChange?.('upload')}><Upload className="h-4 w-4 mr-2" />Upload Data</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Modal */}
      {isViewModalOpen && selectedMaterial && (
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5" />{selectedMaterial.name || 'Material Details'}</DialogTitle>
              <DialogDescription>Comprehensive view of all material information and metadata</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className="text-sm font-medium text-gray-700">Material Name</Label><p className="text-sm font-medium">{selectedMaterial.name || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Category</Label><div className="flex items-center gap-2">{getCategoryIcon(selectedMaterial.category)}<Badge variant="outline">{selectedMaterial.category || 'N/A'}</Badge></div></div>
                  <div><Label className="text-sm font-medium text-gray-700">Subcategory</Label><p className="text-sm">{selectedMaterial.subcategory || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Density</Label><p className="text-sm font-semibold">{selectedMaterial.custom_density || selectedMaterial.density || 'N/A'} {selectedMaterial.unit || ''}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Unit</Label><p className="text-sm">{selectedMaterial.unit || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Description</Label><p className="text-sm">{selectedMaterial.description || 'N/A'}</p></div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-green-800">Environmental Impact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className="text-sm font-medium text-green-700">Carbon Footprint</Label><p className="text-sm font-semibold">{selectedMaterial.custom_carbon_footprint || selectedMaterial.carbon_footprint || 'N/A'} kg CO2e</p></div>
                  <div><Label className="text-sm font-medium text-green-700">Emission Scope</Label>{selectedMaterial.custom_metadata?.scope ? <Badge className={getScopeColor(selectedMaterial.custom_metadata.scope)}>{selectedMaterial.custom_metadata.scope}</Badge> : <p className="text-sm text-gray-500">N/A</p>}</div>
                  <div><Label className="text-sm font-medium text-green-700">Sustainability Rating</Label>
                    {selectedMaterial.custom_metadata?.sustainability_rating
                      ? <div className="flex items-center gap-2"><div className="flex">{renderStars(selectedMaterial.custom_metadata.sustainability_rating)}</div><Badge className={getSustainabilityColor(selectedMaterial.custom_metadata.sustainability_rating)}>{selectedMaterial.custom_metadata.sustainability_rating}/5</Badge></div>
                      : <p className="text-sm text-gray-500">N/A</p>}
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-blue-800">Economic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-sm font-medium text-blue-700">Cost per Unit</Label><p className="text-sm font-semibold">{selectedMaterial.custom_cost_per_unit || selectedMaterial.cost_per_unit || 'N/A'} {selectedMaterial.currency || 'USD'}</p></div>
                  <div><Label className="text-sm font-medium text-blue-700">Currency</Label><p className="text-sm">{selectedMaterial.currency || 'N/A'}</p></div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-green-800">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-sm font-medium text-green-700">Notes</Label><p className="text-sm whitespace-pre-wrap">{selectedMaterial.custom_metadata?.notes || 'No notes available'}</p></div>
                  <div><Label className="text-sm font-medium text-green-700">Data Source</Label><p className="text-sm">{selectedMaterial.custom_metadata?.data_source || 'N/A'}</p></div>
                </div>
              </div>

              <div className="bg-gray-100 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">System Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className="text-sm font-medium text-gray-700">Material ID</Label><p className="text-sm font-mono">{selectedMaterial.id || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Library ID</Label><p className="text-sm font-mono">{selectedMaterial.material_id || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Organization ID</Label><p className="text-sm font-mono">{selectedMaterial.organization_id || 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Created At</Label><p className="text-sm">{selectedMaterial.created_at ? new Date(selectedMaterial.created_at).toLocaleString() : 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Updated At</Label><p className="text-sm">{selectedMaterial.updated_at ? new Date(selectedMaterial.updated_at).toLocaleString() : 'N/A'}</p></div>
                  <div><Label className="text-sm font-medium text-gray-700">Custom Material</Label><Badge variant={selectedMaterial.is_custom ? 'default' : 'secondary'}>{selectedMaterial.is_custom ? 'Yes' : 'No'}</Badge></div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedMaterial && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" />Edit Material: {selectedMaterial.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Density</Label>
                  <Input type="number" placeholder="Enter density" value={editFormData.custom_density} onChange={(e) => setEditFormData(prev => ({ ...prev, custom_density: e.target.value }))} step="0.01" />
                </div>
                <div className="space-y-2">
                  <Label>Carbon Footprint (kg CO₂e)</Label>
                  <Input type="number" placeholder="Enter carbon footprint" value={editFormData.custom_carbon_footprint} onChange={(e) => setEditFormData(prev => ({ ...prev, custom_carbon_footprint: e.target.value }))} step="0.01" />
                </div>
                <div className="space-y-2">
                  <Label>Cost per Unit</Label>
                  <Input type="number" placeholder="Enter cost" value={editFormData.custom_cost_per_unit} onChange={(e) => setEditFormData(prev => ({ ...prev, custom_cost_per_unit: e.target.value }))} step="0.01" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Input placeholder="Enter unit" value={editFormData.unit} onChange={(e) => setEditFormData(prev => ({ ...prev, unit: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Input placeholder="Enter currency" value={selectedMaterial.currency || 'USD'} disabled />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" value={editFormData.notes} onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))} placeholder="Additional notes or context" rows={3} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setSelectedMaterial(null); }}>Cancel</Button>
                <Button onClick={handleSaveEdit}>Save Changes</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{materialToDelete?.name}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setMaterialToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
